import {useEffect, useState} from "react";

export const GTM_ID = "GTM-T2NZSCQM";

const COOKIE_NAME = "acb_consent";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const CHANGE_EVENT = "acb-consent-change";
const OPEN_PREFERENCES_EVENT = "acb-consent-open-preferences";

// Bump when a new tracker is added or a category changes meaning — an old stored
// decision no longer covers it, so we ask again instead of silently loading
// something the visitor never agreed to.
// v2: the separate "marketing" category was folded into "analytics".
export const CONSENT_VERSION = 2;

export const CATEGORIES = ["necessary", "functional", "analytics"];

const DENIED = {
    necessary: true,
    functional: false,
    analytics: false,
};

const isBrowser = () => typeof window !== "undefined";

let cache = null;

const readCookie = () => {
    const match = document.cookie.split("; ").find((row) => row.startsWith(`${COOKIE_NAME}=`));
    return match ? decodeURIComponent(match.slice(COOKIE_NAME.length + 1)) : null;
};

const readStored = () => {
    let raw = null;

    try {
        raw = readCookie();
    } catch (e) {
        raw = null;
    }

    if (!raw) {
        // Cookie may have been stripped by a privacy extension — fall back to the mirror.
        try {
            raw = window.localStorage.getItem(COOKIE_NAME);
        } catch (e) {
            raw = null;
        }
    }

    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== CONSENT_VERSION) return null;
        return {...DENIED, ...parsed, necessary: true};
    } catch (e) {
        return null;
    }
};

const writeStored = (value) => {
    const raw = JSON.stringify(value);

    try {
        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(raw)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
    } catch (e) {
        // ignore
    }

    try {
        window.localStorage.setItem(COOKIE_NAME, raw);
    } catch (e) {
        // ignore
    }
};

export const hasDecision = () => {
    if (!isBrowser()) return false;
    if (cache === null) cache = readStored();
    return Boolean(cache);
};

export const getConsent = () => {
    if (!isBrowser()) return DENIED;
    if (cache === null) cache = readStored();
    return cache || DENIED;
};

export const isAllowed = (category) => {
    if (category === "necessary") return true;
    return Boolean(getConsent()[category]);
};

const gtag = function () {
    // Consent Mode requires the raw `arguments` object, not a spread array.
    window.dataLayer.push(arguments);
};

// The advertising signals ride on `analytics`: the banner offers a single
// measurement toggle, so one choice governs everything in the GTM container.
const consentSignals = (prefs) => ({
    ad_storage: prefs.analytics ? "granted" : "denied",
    ad_user_data: prefs.analytics ? "granted" : "denied",
    ad_personalization: prefs.analytics ? "granted" : "denied",
    analytics_storage: prefs.analytics ? "granted" : "denied",
    functionality_storage: prefs.functional ? "granted" : "denied",
    personalization_storage: prefs.functional ? "granted" : "denied",
    security_storage: "granted",
});

let gtmLoaded = false;

const loadGTM = () => {
    if (gtmLoaded || !isBrowser()) return;
    if (document.getElementById("acb-gtm")) {
        gtmLoaded = true;
        return;
    }

    gtmLoaded = true;

    window.dataLayer.push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
    });

    const script = document.createElement("script");
    script.id = "acb-gtm";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(script);
};

const applyConsent = (prefs) => {
    gtag("consent", "update", consentSignals(prefs));

    if (prefs.analytics) loadGTM();
};

/**
 * Must run before any tag: sets Consent Mode v2 defaults to denied, then replays
 * a stored decision if the visitor already made one.
 */
export const initConsentMode = () => {
    if (!isBrowser() || window.__acbConsentInit) return;
    window.__acbConsentInit = true;

    window.dataLayer = window.dataLayer || [];
    gtag("consent", "default", {...consentSignals(DENIED), wait_for_update: 500});

    if (hasDecision()) applyConsent(getConsent());
};

export const setConsent = (prefs) => {
    if (!isBrowser()) return DENIED;

    const next = {
        ...DENIED,
        ...prefs,
        necessary: true,
        version: CONSENT_VERSION,
    };

    cache = next;
    writeStored(next);
    applyConsent(next);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, {detail: next}));

    return next;
};

export const acceptAll = () => setConsent({functional: true, analytics: true});

export const rejectAll = () => setConsent({functional: false, analytics: false});

/** Grants a single category — used by the inline "Load" button on blocked embeds. */
export const grantCategory = (category) => setConsent({...getConsent(), [category]: true});

export const openPreferences = () => {
    if (!isBrowser()) return;
    window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT));
};

export const onOpenPreferences = (handler) => {
    if (!isBrowser()) return () => {};
    window.addEventListener(OPEN_PREFERENCES_EVENT, handler);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, handler);
};

/**
 * Consent state for React. Always returns the denied defaults on the first render
 * so server and client markup match, then syncs in an effect.
 */
export const useConsent = () => {
    const [state, setState] = useState({consent: DENIED, decided: false, ready: false});

    useEffect(() => {
        const sync = () => setState({consent: getConsent(), decided: hasDecision(), ready: true});

        sync();
        window.addEventListener(CHANGE_EVENT, sync);
        return () => window.removeEventListener(CHANGE_EVENT, sync);
    }, []);

    return state;
};
